import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { 
  Lock, AlertTriangle, CheckCircle, XCircle, TrendingUp, 
  Loader2, ArrowRight, Mail, User as UserIcon, LockKeyhole, 
  Sparkles, Zap, Eye, EyeOff, Crown, Download,
  ChevronRight, Shield, FileText
} from "lucide-react";
import { trackViewContent, trackInitiateCheckout, trackPurchase, trackRegistration, getMetaAttributionPayload } from "@/utils/metaPixel";
import { getUTMPayload } from "@/utils/utmUtils";
import { useMarketConfig, usePricingForMarket, invalidateMarketConfig, price as fmtPrice } from "@/utils/marketConfig";
import LPFooter from "@/components/LPFooter";
import { companyOptions, joiningOptions } from "@/data/caraQuestions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function contentDispositionFilename(headers) {
  const disposition = headers?.["content-disposition"]
    || headers?.get?.("content-disposition")
    || "";
  if (!disposition) return "";

  const encoded = disposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  const quoted = disposition.match(/filename\s*=\s*"([^"]+)"/i);
  const bare = disposition.match(/filename\s*=\s*([^;\s]+)/i);
  let filename = encoded?.[1] || quoted?.[1] || bare?.[1] || "";
  if (encoded?.[1]) {
    try { filename = decodeURIComponent(filename); } catch { /* retain safe raw value */ }
  }
  return filename
    .split(/[\\/]/)
    .pop()
    .replace(/[<>:"|?*\u0000-\u001F]/g, "_")
    .trim();
}

// Same questionnaire family as ChatbotPage — market-aware (India byte-identical;
// see data/caraQuestions.js: labels must keep resolve_company_key's keywords).
const buildLpQuestions = (market) => [
  {
    id: "target_company",
    text: "Where do you want to work next?",
    options: companyOptions(market)
  },
  {
    id: "experience",
    text: "What is your current work status?",
    options: ["Fresher", "Junior", "Senior/Lead", "Career Gap"]
  },
  {
    id: "joining_time",
    text: "How soon can you start the new job?",
    options: joiningOptions(market)
  },
  {
    id: "skill_set",
    text: "What is your strongest area of work?",
    options: ["Technical", "Management", "Creative", "Administrative"]
  },
  {
    id: "career_goal",
    text: "What is the biggest goal for this resume?",
    options: ["Salary Hike", "Industry Change", "Role Promotion", "First Job"]
  },
  {
    id: "target_field",
    text: "Which field are you targeting for your next job?",
    options: ["AI & Data", "Remote/Global Ops", "Marketing & Growth", "Engineering & EV", "Full-Stack Web"]
  }
];

// Score Gauge Component
function ScoreGauge({ score, category, size = "large" }) {
  const radius = size === "large" ? 45 : 35;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score <= 40 ? "#ef4444" : score <= 70 ? "#eab308" : "#16a34a";
  const sizeClass = size === "large" ? "w-48 h-48" : "w-32 h-32";
  const textSize = size === "large" ? "text-4xl" : "text-2xl";

  return (
    <div className={`relative ${sizeClass} mx-auto`}>
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="8" />
        <circle
          cx="50" cy="50" r={radius} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" className="transition-all duration-[1500ms] ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`font-['JetBrains_Mono'] ${textSize} font-bold text-[#0A1128]`}>{score}</span>
        <span className="text-xs text-gray-500 uppercase tracking-wider">/100</span>
        <span className={`mt-1 text-xs px-2 py-0.5 rounded-full ${
          score <= 40 ? 'bg-red-500/20 text-red-500' : 
          score <= 70 ? 'bg-yellow-500/20 text-yellow-500' : 
          'bg-green-500/20 text-green-500'
        }`}>
          {category}
        </span>
      </div>
    </div>
  );
}

export default function LPResultsPage({ lpSlug = "", lpName = "Professional" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, getHeaders, setUser } = useAuth();
  // Localized pricing (geo / region selector); India stays ₹ via the fallback.
  const { cfg: mkt, ready: mktReady } = useMarketConfig();
  const p = (amt) => (mktReady ? fmtPrice(mkt, amt) : "…");
  const QUESTIONS = React.useMemo(() => buildLpQuestions(mkt.market), [mkt.market]);

  // Flow state: fake_result -> payment -> questionnaire -> analyzing -> real_result -> upsell -> account
  const [step, setStep] = useState("loading");
  const [analysis, setAnalysis] = useState(null);
  // The unlock is charged in the market stamped on THIS analysis — price the pay
  // CTAs from it so button == Stripe charge even if the visitor's geo changed.
  const { cfg: chargeCfg, ready: chargeReady } = usePricingForMarket(analysis?.market, mkt, mktReady);
  const pCharge = (amt) => (chargeReady ? fmtPrice(chargeCfg, amt) : "…");
  const [loading, setLoading] = useState(true);
  
  // Payment state
  const [email, setEmail] = useState("");
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Questionnaire state
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});
  const [analyzing, setAnalyzing] = useState(false);
  
  // Account creation state
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);
  
  // Get session ID - use same as AuthContext for platform consistency
  const getSessionId = () => {
    let sid = localStorage.getItem("pos_session");
    if (!sid) {
      sid = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("pos_session", sid);
    }
    return sid;
  };
  
  // Check if Purchase was already tracked for this analysis (persist across re-renders)
  const hasPurchaseBeenTracked = (analysisId) => {
    const tracked = localStorage.getItem(`lp_purchase_tracked_${analysisId}`);
    return tracked === "true";
  };
  
  const markPurchaseTracked = (analysisId) => {
    localStorage.setItem(`lp_purchase_tracked_${analysisId}`, "true");
  };
  
  // Set email from logged-in user
  // Set email from logged-in user
  useEffect(() => {
    if (user?.email && !email) {
      setEmail(user.email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Fetch analysis on mount
  useEffect(() => {
    const analysisId = searchParams.get("analysis_id") || location.state?.analysisId;
    const paid = searchParams.get("paid") === "true";
    const stripeSessionId = searchParams.get("session_id");
    
    if (!analysisId) {
      navigate(`/${lpSlug || ""}`);
      return;
    }
    
    fetchAnalysis(analysisId, paid, stripeSessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, location.state]);

  const fetchAnalysis = async (analysisId, isPaid = false, stripeSessionId = null) => {
    try {
      const headers = { "X-Session-ID": getSessionId() };
      if (user) {
        Object.assign(headers, getHeaders());
      }
      
      // If returning from payment, verify payment first
      if (isPaid && stripeSessionId) {
        try {
          const verifyRes = await axios.post(
            `${API}/lp/verify-payment?analysis_id=${analysisId}&stripe_session_id=${stripeSessionId}`,
            getMetaAttributionPayload(),
            { headers }
          );
          if (verifyRes.data.payment_status === "paid") {
            if (!user?.email) {
              setEmail(verifyRes.data.guest_email || "");
            }
            // Track Purchase event - only once per analysis (persisted in localStorage)
            if (!hasPurchaseBeenTracked(analysisId)) {
              const metaEventId = verifyRes.data.meta_event_id || `purchase_${stripeSessionId}`;
              trackPurchase(chargeCfg.prices.one_time, chargeCfg.currency, "Resume Analysis", { eventId: metaEventId, clientOnly: true });
              markPurchaseTracked(analysisId);
            }
          }
        } catch (err) {
          console.error("Payment verification failed:", err);
        }
      }
      
      // Use existing analysis endpoint
      const res = await axios.get(`${API}/lp/analysis/${analysisId}`, { headers });
      setAnalysis(res.data);
      
      // Track ViewContent for analysis page - only once per session
      const viewKey = `lp_view_tracked_${analysisId}`;
      if (!sessionStorage.getItem(viewKey)) {
        trackViewContent("Resume Analysis Results", lpSlug || "general");
        sessionStorage.setItem(viewKey, "true");
      }
      
      // Determine which step to show
      if (res.data.status === "complete") {
        setStep("real_result");
      } else if (res.data.payment_status === "paid") {
        // Paid but not complete - go to questionnaire
        setStep("questionnaire");
      } else {
        // Not paid - show fake result
        setStep("fake_result");
      }
    } catch (err) {
      console.error("Failed to fetch analysis:", err);
      toast.error("Failed to load analysis");
      navigate(`/${lpSlug || ""}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle payment - use guest checkout or authenticated checkout
  const handlePayment = async () => {
    const paymentEmail = user?.email || email;
    if (!paymentEmail || !paymentEmail.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }

    // Track InitiateCheckout event
    trackInitiateCheckout(chargeCfg.prices.one_time, chargeCfg.currency, "Resume Analysis");
    
    setProcessingPayment(true);
    try {
      const headers = { "X-Session-ID": getSessionId() };
      if (user) {
        Object.assign(headers, getHeaders());
      }
      
      const res = await axios.post(`${API}/lp/guest-checkout`, {
        analysis_id: analysis.id,
        email: paymentEmail,
        origin_url: window.location.origin,
        lp_slug: lpSlug,
        ...getUTMPayload(),
        ...getMetaAttributionPayload(),
      }, { headers });
      
      // Redirect to Stripe
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment failed");
      setProcessingPayment(false);
    }
  };

  // Handle questionnaire response
  const handleQuestionResponse = (answer) => {
    const question = QUESTIONS[currentQuestion];
    const newResponses = { ...responses, [question.id]: answer };
    setResponses(newResponses);
    
    if (currentQuestion < QUESTIONS.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      // All questions answered - run AI analysis using unlock endpoint
      runRealAnalysis(newResponses);
    }
  };

  const runRealAnalysis = async (allResponses) => {
    setAnalyzing(true);
    setStep("analyzing");
    
    try {
      const headers = { "X-Session-ID": getSessionId() };
      if (user) Object.assign(headers, getHeaders());
      
      // If not logged in, create (or recover) account using lp/create-account
      if (!user) {
        const tempPassword = `Temp${Date.now()}!`;
        try {
          const accountRes = await axios.post(`${API}/lp/create-account`, {
            analysis_id: analysis.id,
            email: email || analysis.guest_email,
            password: tempPassword,
            name: ""
          }, { headers });
          
          localStorage.setItem("token", accountRes.data.token);
          // Mirror into the app-wide token key so the session survives into the
          // main app (AuthContext restore) and /market-config answers with the
          // ACCOUNT market from here on, then drop the geo-resolved config.
          localStorage.setItem("pos_token", accountRes.data.token);
          invalidateMarketConfig();
          setUser(accountRes.data.user);
          setPassword(tempPassword);
          headers["Authorization"] = `Bearer ${accountRes.data.token}`;
        } catch (err) {
          // lp/create-account returns the user token even if email exists (paid flow)
          // But if it fails for any reason, check if we already have a token in localStorage
          const existingToken = localStorage.getItem("token");
          if (existingToken) {
            headers["Authorization"] = `Bearer ${existingToken}`;
          } else {
            // No way to authenticate — need user to log in manually
            toast.error("Please log in to see your full analysis");
            setStep("account");
            setAnalyzing(false);
            return;
          }
        }
      }
      
      // The unlock endpoint runs the full AI pipeline (score + report + rewrite)
      const token = headers["Authorization"]?.replace("Bearer ", "") || localStorage.getItem("token");
      const unlockRes = await axios.post(`${API}/analysis/${analysis.id}/unlock`, {}, {
        headers: { ...headers, "Authorization": token ? `Bearer ${token}` : undefined }
      });
      
      setAnalysis(unlockRes.data);
      toast.success("Your ATS-optimised resume is ready!");
      setStep("real_result");
    } catch (err) {
      console.error("AI analysis failed:", err);
      const status = err.response?.status;
      if (status === 401) {
        toast.error("Please log in to see your full analysis");
        setStep("account");
      } else {
        toast.error(err.response?.data?.detail || "Analysis failed. Please try again.");
        setStep("real_result");
      }
    } finally {
      setAnalyzing(false);
    }
  };

  // Handle account password update
  const handleCreateAccount = async () => {
    if (!password || password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setCreatingAccount(true);
    try {
      const headers = { "X-Session-ID": getSessionId() };
      
      const res = await axios.post(`${API}/lp/create-account`, {
        analysis_id: analysis.id,
        email: email || analysis.guest_email,
        password: password,
        name: ""
      }, { headers });
      
      localStorage.setItem("token", res.data.token);
      // Same mirroring as the paid-unlock path: app-wide session + account market.
      localStorage.setItem("pos_token", res.data.token);
      invalidateMarketConfig();
      setUser(res.data.user);

      // Track CompleteRegistration event
      if (res.data.is_new) {
        trackRegistration("lp_flow");
      }
      
      toast.success(res.data.is_new ? "Account created!" : "Logged in!");
      setStep("upsell");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create account");
    } finally {
      setCreatingAccount(false);
    }
  };

  // Navigate to season pass
  const handleSeasonPassClick = () => {
    navigate("/season-pass");
  };

  // Download resume as proper DOCX
  const handleDownload = async () => {
    if (!analysis?.rewritten_resume?.improved_resume) {
      toast.error("No optimized resume available");
      return;
    }
    try {
      const documentLabel = chargeCfg.doc_term === "CV" ? "CV" : "Resume";
      toast.loading(`Generating your DOCX ${documentLabel}...`, { id: "docx-dl" });
      const headers = { "X-Session-ID": getSessionId() };
      if (user) Object.assign(headers, getHeaders());
      const res = await axios.post(
        `${API}/resume/render-docx`,
        {
          analysis_id: analysis.id,
          resume_text: analysis.rewritten_resume.improved_resume,
        },
        { headers, responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      }));
      const a = document.createElement("a");
      a.href = url;
      a.download = contentDispositionFilename(res.headers)
        || `ATS_Optimized_${documentLabel}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("DOCX downloaded!", { id: "docx-dl" });
    } catch (err) {
      // fallback to plain text
      const blob = new Blob([analysis.rewritten_resume.improved_resume], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const documentLabel = chargeCfg.doc_term === "CV" ? "CV" : "Resume";
      a.download = `ATS_Optimized_${documentLabel}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Resume downloaded!", { id: "docx-dl" });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-[#FF6B35] animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your analysis...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12 bg-gray-50">
      <div className="max-w-3xl mx-auto px-4">
        
        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {["Result", "Payment", "Questions", "AI Analysis", "Unlock More"].map((label, idx) => {
            const stepOrder = ["fake_result", "payment", "questionnaire", "analyzing", "real_result", "upsell", "account"];
            const currentIdx = stepOrder.indexOf(step);
            const isActive = idx <= currentIdx;
            const isCurrent = idx === currentIdx || (step === "analyzing" && idx === 3);
            return (
              <React.Fragment key={label}>
                {idx > 0 && <div className={`w-8 h-0.5 ${isActive ? "bg-[#FF6B35]" : "bg-gray-300"}`} />}
                <div className={`flex items-center gap-1 ${isCurrent ? "text-[#FF6B35] font-semibold" : isActive ? "text-gray-700" : "text-gray-400"}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                    isCurrent ? "bg-[#FF6B35] text-white" : isActive ? "bg-[#FF6B35]/20 text-[#FF6B35]" : "bg-gray-200"
                  }`}>
                    {idx + 1}
                  </div>
                  <span className="text-xs hidden sm:inline">{label}</span>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* STEP 1: Fake/Teaser Result */}
        {step === "fake_result" && analysis && (
          <div className="space-y-6">
            {/* Score Card */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-100">
              <h2 className="text-center text-2xl font-bold text-[#0A1128] mb-2">Your ATS Score</h2>
              <p className="text-center text-gray-500 mb-6">Preliminary analysis of your resume</p>
              <ScoreGauge score={analysis.score} category={analysis.category} />
              <p className="text-center text-gray-600 mt-4 text-sm italic">"{analysis.summary}"</p>
              {analysis.detected_role && (
                <p className="text-center text-[#FF6B35] mt-2 text-sm font-medium">
                  Detected Role: {analysis.detected_role || analysis.target_role}
                </p>
              )}
            </div>

            {/* Flaws (Visible) */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-gray-100">
              <h3 className="font-bold text-[#0A1128] mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Critical Issues Found
              </h3>
              <div className="space-y-3">
                {analysis.flaws?.slice(0, 3).map((flaw, i) => (
                  <div key={i} className="p-4 bg-red-50 rounded-lg border border-red-100">
                    <div className="flex items-center gap-2 mb-1">
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span className="font-semibold text-[#0A1128] text-sm">{flaw.title}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        flaw.severity === 'critical' ? 'bg-red-500/20 text-red-500' : 
                        flaw.severity === 'major' ? 'bg-yellow-500/20 text-yellow-500' : 
                        'bg-blue-500/20 text-blue-500'
                      }`}>{flaw.severity}</span>
                    </div>
                    <p className="text-gray-600 text-xs ml-6">{flaw.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Locked Content Preview */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-[#FF6B35]/30 relative overflow-hidden">
              <div className="absolute inset-0 backdrop-blur-sm bg-white/70 z-10 flex flex-col items-center justify-center">
                <Lock className="w-10 h-10 text-[#FF6B35] mb-3" />
                <h3 className="text-lg font-bold text-[#0A1128] mb-1">Unlock Full Analysis</h3>
                <p className="text-gray-500 text-sm mb-4">Get AI-powered fixes & rewritten resume</p>
                <button
                  onClick={() => setStep("payment")}
                  className="bg-[#FF6B35] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#e55a2b] transition-all flex items-center gap-2"
                >
                  Unlock for {pCharge(chargeCfg.prices.one_time)} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
              <div className="opacity-30">
                <h3 className="font-bold mb-4">Detailed Report</h3>
                <p className="text-sm text-gray-600 mb-2">Your resume needs improvements in 7 key areas...</p>
                <p className="text-sm text-gray-600">AI-rewritten resume with 12 targeted changes...</p>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: Payment */}
        {step === "payment" && (
          <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-[#FF6B35]/30">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#FF6B35]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-[#FF6B35]" />
              </div>
              <h2 className="text-2xl font-bold text-[#0A1128] mb-2">Unlock Your Full Analysis</h2>
              <p className="text-gray-500">One-time payment • Instant delivery</p>
            </div>

            {/* What you get */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <h4 className="font-semibold text-[#0A1128] mb-3">What's included:</h4>
              <ul className="space-y-2">
                {[
                  "Real AI-powered ATS scoring (GPT-4o)",
                  "Detailed analysis report with fixes",
                  "Copy-paste fix recommendations",
                  "AI-rewritten optimized resume",
                  "Keyword optimization suggestions"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Email input - only show if not logged in */}
            {!user ? (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#FF6B35] focus:outline-none"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">We'll send your report to this email</p>
              </div>
            ) : (
              <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-green-700 font-medium">Logged in as {user.email}</span>
                </div>
              </div>
            )}

            {/* Price & CTA */}
            <div className="text-center mb-6">
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="text-gray-400 line-through text-lg">{chargeReady ? fmtPrice(chargeCfg, chargeCfg.prices.one_time_was) : "…"}</span>
                <span className="text-4xl font-bold text-[#0A1128]">{pCharge(chargeCfg.prices.one_time)}</span>
              </div>
              <span className="text-xs text-[#FF6B35] font-semibold">90% OFF - Launch Offer</span>
            </div>

            <button
              onClick={handlePayment}
              disabled={processingPayment || !email}
              className="w-full bg-[#FF6B35] text-white py-4 rounded-lg font-bold text-lg hover:bg-[#e55a2b] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {processingPayment ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Shield className="w-5 h-5" />
                  Pay {pCharge(chargeCfg.prices.one_time)} Securely
                </>
              )}
            </button>

            <div className="flex items-center justify-center gap-4 mt-4 text-xs text-gray-500">
              <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> Secure</span>
              <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> SSL Encrypted</span>
            </div>
          </div>
        )}

        {/* STEP 3: Questionnaire */}
        {step === "questionnaire" && (
          <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-100">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full mb-4">
                <CheckCircle className="w-4 h-4" />
                Payment Successful!
              </div>
              <h2 className="text-2xl font-bold text-[#0A1128] mb-2">Quick Questions</h2>
              <p className="text-gray-500">Help us personalize your AI analysis</p>
            </div>

            {/* Progress */}
            <div className="mb-6">
              <div className="flex justify-between text-xs text-gray-500 mb-2">
                <span>Question {currentQuestion + 1} of {QUESTIONS.length}</span>
                <span>{Math.round(((currentQuestion + 1) / QUESTIONS.length) * 100)}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full">
                <div 
                  className="h-full bg-[#FF6B35] rounded-full transition-all duration-300"
                  style={{ width: `${((currentQuestion + 1) / QUESTIONS.length) * 100}%` }}
                />
              </div>
            </div>

            {/* Current Question */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-[#0A1128] mb-4">
                {QUESTIONS[currentQuestion].text}
              </h3>
              <div className="grid gap-3">
                {QUESTIONS[currentQuestion].options.map((option) => (
                  <button
                    key={option}
                    onClick={() => handleQuestionResponse(option)}
                    className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-[#FF6B35] hover:bg-[#FF6B35]/5 transition-all flex items-center justify-between group"
                  >
                    <span className="text-[#0A1128]">{option}</span>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#FF6B35]" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* STEP 3.5: Analyzing */}
        {step === "analyzing" && (
          <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-100">
            <div className="text-center py-12">
              <Loader2 className="w-12 h-12 text-[#FF6B35] animate-spin mx-auto mb-4" />
              <h3 className="text-xl font-bold text-[#0A1128] mb-2">AI Analyzing Your Resume...</h3>
              <p className="text-gray-500">This takes about 30-60 seconds</p>
              <div className="mt-6 space-y-2">
                <div className="flex items-center gap-2 justify-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Scanning for ATS compatibility
                </div>
                <div className="flex items-center gap-2 justify-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Analyzing keyword density
                </div>
                <div className="flex items-center gap-2 justify-center text-sm text-gray-600">
                  <Loader2 className="w-4 h-4 text-[#FF6B35] animate-spin" /> Generating optimized resume
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 4: Real Result */}
        {step === "real_result" && analysis && (
          <div className="space-y-6">
            {/* Success Banner */}
            {analysis.status === "complete" && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <span className="text-green-700 font-medium">Full AI Analysis Complete!</span>
              </div>
            )}

            {/* Score Card */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-100">
              <h2 className="text-center text-2xl font-bold text-[#0A1128] mb-2">
                {analysis.status === "complete" ? "Your Real ATS Score" : "Your ATS Score"}
              </h2>
              <ScoreGauge score={analysis.score} category={analysis.category} />
              <p className="text-center text-gray-600 mt-4 text-sm">{analysis.summary}</p>
            </div>

            {/* Flaws with fixes */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-gray-100">
              <h3 className="font-bold text-[#0A1128] mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Issues & How to Fix Them
              </h3>
              <div className="space-y-3">
                {analysis.flaws?.map((flaw, i) => (
                  <div key={i} className="p-4 bg-red-50 rounded-lg border border-red-100">
                    <div className="flex items-center gap-2 mb-1">
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span className="font-semibold text-[#0A1128] text-sm">{flaw.title}</span>
                    </div>
                    <p className="text-gray-600 text-sm ml-6">{flaw.description}</p>
                    {flaw.fix && (
                      <p className="text-green-600 text-sm ml-6 mt-1">💡 Fix: {flaw.fix}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Report Section */}
            {analysis.report && (
              <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-blue-200">
                <h3 className="font-bold text-[#0A1128] mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-500" />
                  Detailed Report
                </h3>
                <p className="text-gray-700 mb-4">{analysis.report.executive_summary}</p>
                {analysis.report.recommendations?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm">Key Recommendations:</h4>
                    <ul className="space-y-2">
                      {analysis.report.recommendations.slice(0, 5).map((rec, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                          <div>
                            <span className="font-medium text-[#0A1128]">{typeof rec === 'string' ? rec : rec.title}</span>
                            {typeof rec === 'object' && rec.description && (
                              <p className="text-gray-500 text-xs mt-0.5">{rec.description}</p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Rewritten Resume */}
            {analysis.rewritten_resume?.improved_resume && (
              <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-green-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-[#0A1128] flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-green-500" />
                    AI-Optimized Resume
                  </h3>
                  <button
                    onClick={handleDownload}
                    className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all text-sm"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
                  <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono">
                    {analysis.rewritten_resume.improved_resume.substring(0, 1500)}...
                  </pre>
                </div>
                {analysis.rewritten_resume.changes_made?.length > 0 && (
                  <div className="mt-4">
                    <h4 className="font-semibold text-sm mb-2">Changes Made:</h4>
                    <ul className="space-y-1">
                      {analysis.rewritten_resume.changes_made.slice(0, 5).map((change, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                          <Sparkles className="w-4 h-4 text-green-500 mt-0.5" />
                          {change}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Continue Button */}
            {!user ? (
              <button
                onClick={() => setStep("account")}
                className="w-full bg-[#FF6B35] text-white py-4 rounded-lg font-bold text-lg hover:bg-[#e55a2b] transition-all flex items-center justify-center gap-2"
              >
                Save Your Results <ArrowRight className="w-5 h-5" />
              </button>
            ) : (
              <div className="space-y-3">
                <button
                  onClick={() => navigate("/templates")}
                  className="w-full bg-[#2832C2] text-white py-4 rounded-lg font-bold text-lg hover:bg-[#1e28a0] transition-all flex items-center justify-center gap-2"
                >
                  <FileText className="w-5 h-5" />
                  View in Resume Vault
                </button>
                <button
                  onClick={() => setStep("upsell")}
                  className="w-full bg-[#FF6B35] text-white py-3 rounded-lg font-semibold hover:bg-[#e55a2b] transition-all flex items-center justify-center gap-2"
                >
                  Continue <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* STEP 5: Account Creation */}
        {step === "account" && (
          <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-100">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#FF6B35]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserIcon className="w-8 h-8 text-[#FF6B35]" />
              </div>
              <h2 className="text-2xl font-bold text-[#0A1128] mb-2">Save Your Results</h2>
              <p className="text-gray-500">Create a password to access your analysis anytime</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={email || analysis?.guest_email || ""}
                    disabled
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Create Password</label>
                <div className="relative">
                  <LockKeyhole className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a password (min 6 chars)"
                    className="w-full pl-10 pr-12 py-3 border-2 border-gray-200 rounded-lg focus:border-[#FF6B35] focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                onClick={handleCreateAccount}
                disabled={creatingAccount || password.length < 6}
                className="w-full bg-[#FF6B35] text-white py-4 rounded-lg font-bold text-lg hover:bg-[#e55a2b] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {creatingAccount ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    Create Account & Continue
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* STEP 6: Upsell */}
        {step === "upsell" && (
          <div className="space-y-6">
            {/* Congrats Banner */}
            <div className="bg-gradient-to-r from-[#FF6B35] to-[#e55a2b] rounded-2xl p-6 text-white text-center">
              <Sparkles className="w-10 h-10 mx-auto mb-3" />
              <h2 className="text-2xl font-bold mb-2">Your Resume is Now Optimized!</h2>
              <p className="text-white/90">But there's more you can do to land your dream job faster</p>
            </div>

            {/* Resume Roast Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0A1128]">Resume Roast</h3>
                  <p className="text-gray-500 text-sm">{pCharge(chargeCfg.prices.one_time)} one-time</p>
                </div>
                <CheckCircle className="w-6 h-6 text-green-500 ml-auto" />
              </div>
              <p className="text-gray-600 text-sm">✓ Already included in your purchase</p>
            </div>

            {/* Season Pass Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border-4 border-[#2832C2] relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-[#2832C2] text-white text-xs px-3 py-1 rounded-bl-lg font-semibold">
                RECOMMENDED
              </div>
              
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-[#2832C2]/10 rounded-lg flex items-center justify-center">
                  <Crown className="w-6 h-6 text-[#2832C2]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0A1128]">Full PlacementOS Access</h3>
                  <p className="text-gray-500 text-sm">{p(mkt.prices.pass_annual)} for lifetime access</p>
                </div>
              </div>

              <ul className="space-y-2 mb-6">
                {[
                  "AI Career Companion (unlimited chats)",
                  "Smart Job Matching & Recommendations",
                  "Career Velocity Simulator",
                  "Skill Path & Learning Roadmap",
                  "5 Resume Analyses",
                  "Priority Support"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-[#2832C2]" />
                    {item}
                  </li>
                ))}
              </ul>

              <button
                onClick={handleSeasonPassClick}
                className="w-full bg-[#2832C2] text-white py-4 rounded-lg font-bold hover:bg-[#1e28a0] transition-all flex items-center justify-center gap-2"
              >
                <Crown className="w-5 h-5" />
                Upgrade to Full Access — {p(mkt.prices.pass_annual)}
              </button>
            </div>

            {/* Skip Option */}
            <button
              onClick={() => navigate("/dashboard")}
              className="w-full text-gray-500 hover:text-gray-700 text-sm py-2"
            >
              Maybe later, go to Dashboard →
            </button>
            
            {/* View on Main Results Page */}
            <button
              onClick={() => navigate(`/results?analysis_id=${analysis?.id}`)}
              className="w-full text-gray-400 hover:text-gray-600 text-xs py-1"
            >
              View on main results page
            </button>
          </div>
        )}
      </div>
      <LPFooter />
    </div>
  );
}
